/**
 * Talking to one agent.
 *
 * Three columns, and the third is the one that matters. Conversations on the
 * left because you come back to them; the thread in the middle; and the
 * **evidence** on the right, because the answer is only worth what it cited.
 *
 * The middle column is the Chat page's: the same composer, with the same
 * model picker, `+` menu (files, web search, deep search, research) and hint
 * pills, so nothing has to be learned twice. What differs is underneath —
 * every answer here is drawn from the agent's knowledge graphs, and each
 * per-turn choice (a model, a search, a file) is a choice for *this question*;
 * the agent's own settings, from Configure, remain the default.
 *
 * Citations are live. `[2]` in an answer is a chip you can press, and pressing
 * it scrolls the evidence panel to the record it came from — which is how you
 * check an answer rather than trust it. A refusal is styled as an ordinary
 * answer, not an error, because an agent saying "the graph does not contain
 * this" is the feature working.
 *
 * The thread lives on the server rather than in this tab: a citation that
 * vanished when the tab closed was never a citation.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ErrorNotice, Loading, relativeTime } from '@/shared/components'
import { Composer, Dots, FileIcon, MODES, type Attachment, type ChatMode } from '@/shared/components/Composer'
import { Markdown } from '@/shared/components/Markdown'
import { Dock, DockMain, DockPanel, useDock } from '@/shared/components/dock/Dock'
import { Icon } from '@/shared/components/icons'
import { ModelPicker, type PickerGroup, providerName } from '@/shared/components/ModelPicker'
import { useToast } from '@/shared/components/toast'
import { Chip } from '@/features/brain/components'
import { useReadFile } from '@/features/chat/hooks'
import { AgentDialog } from './AgentDialog'
import {
  useAgent,
  useAgentOptions,
  useAskAgent,
  useConnectionsModels,
  useDeleteThread,
  useThread,
  useThreads,
} from './hooks'
import type { AgentMessage, AgentSource } from './types'
import './agents.css'

/** Where a model comes from: the service key, or one connection. */
type SourceKey = 'service' | string

export function AgentChatPage() {
  const { agentId } = useParams<{ agentId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()

  const agent = useAgent(agentId)
  const options = useAgentOptions()
  const threads = useThreads(agentId)
  const [threadId, setThreadId] = useState<string | undefined>()
  const thread = useThread(agentId, threadId)
  const ask = useAskAgent(agentId ?? '')
  const removeThread = useDeleteThread(agentId ?? '')
  const readFile = useReadFile()

  const [draft, setDraft] = useState('')
  const [configuring, setConfiguring] = useState(false)
  // Conversations and evidence sit beside the thread as dock panels: the
  // evidence is a check on an answer, so it stays minimised until there is some.
  const dock = useDock('agent.chat', { threads: 'open', evidence: 'min' })
  const [focused, setFocused] = useState<AgentSource | null>(null)
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [reading, setReading] = useState(0)
  // The question just sent, shown at once rather than when the answer lands
  // with it. The thread on the server is still the record; this is only the
  // gap between pressing Enter and the server having both halves.
  const [inFlight, setInFlight] = useState<{ question: string; files: string[] } | null>(null)
  const endRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // --- this turn's choices --------------------------------------------------
  //
  // Each is `null` until the person changes it, and null means "as the agent
  // is configured". Kept separate from the agent's own settings on purpose:
  // picking DeepSeek for one hard question must not quietly reconfigure the
  // agent for everyone who asks it something tomorrow.
  const [pick, setPick] = useState<{ source: SourceKey; model: string } | null>(null)
  const [modePick, setModePick] = useState<ChatMode | null>(null)
  const [searchVia, setSearchVia] = useState<string | null>(null)
  // Which graphs this conversation reads. null is the agent's own; [] is none.
  const [graphPick, setGraphPick] = useState<string[] | null>(null)

  const agentDefault = useMemo(
    () => ({
      source: (agent.data?.llm_connection_id || 'service') as SourceKey,
      model: agent.data?.model ?? '',
      mode: (agent.data?.web_search ? 'web' : 'chat') as ChatMode,
      searchVia: agent.data?.search_connection_id || 'service',
    }),
    [agent.data],
  )
  const source = pick?.source ?? agentDefault.source
  const model = pick?.model ?? agentDefault.model
  const mode = modePick ?? agentDefault.mode
  const agentGraphIds = useMemo(() => agent.data?.graph_ids ?? [], [agent.data])
  const graphIds = graphPick ?? agentGraphIds
  const allGraphs = options.data?.graphs ?? []
  const readGraphs = graphIds
    .map((id) => allGraphs.find((g) => g.graph_id === id) ?? agent.data?.graphs.find((g) => g.graph_id === id))
    .filter((g): g is NonNullable<typeof g> => Boolean(g))

  // Reconfiguring the agent resets the turn's choices to its new defaults —
  // the point of Configure is to change what "as configured" means.
  useEffect(() => {
    setPick(null)
    setModePick(null)
    setSearchVia(null)
    setGraphPick(null)
  }, [agentDefault.model, agentDefault.source, agentDefault.mode, agentDefault.searchVia, agentGraphIds])

  // --- models: the service keys, then each connection ---------------------------
  const llmConnections = options.data?.llm_connections ?? []
  const searchConnections = options.data?.search_connections ?? []
  const connectionModels = useConnectionsModels(llmConnections.map((c) => c.connection_id))
  const groups = useMemo(() => {
    const out: PickerGroup[] = []
    if (options.data) {
      out.push({
        key: 'service',
        label: 'Service key',
        sub: options.data.providers?.length
          ? options.data.providers.map(providerName).join(' + ')
          : null,
        models: options.data.models,
        error: options.data.models_configured
          ? options.data.models_error
          : 'Neither GROQ_API_KEY nor HF_TOKEN is set on the API.',
      })
    }
    llmConnections.forEach((c, i) => {
      const q = connectionModels[i]
      out.push({
        key: c.connection_id,
        label: c.label,
        sub: `your ${providerName(c.connector_id)} key`,
        models: q?.data?.models ?? [],
        error: q?.data?.error ?? (q?.error ? String((q.error as Error).message) : null),
        loading: q?.isLoading,
      })
    })
    return out
  }, [options.data, llmConnections, connectionModels])
  const chosen = groups.find((g) => g.key === source)?.models.find((m) => m.model === model)

  // --- search ----------------------------------------------------------------
  const serverSearch = options.data?.web_search?.server_key ?? false
  const searchChoices = useMemo(
    () => [
      ...searchConnections.map((c) => ({ id: c.connection_id, label: c.label, sub: 'Tavily connection' })),
      ...(serverSearch ? [{ id: 'service', label: 'Service key', sub: 'TAVILY_API_KEY on the API' }] : []),
    ],
    [searchConnections, serverSearch],
  )
  const canSearch = searchChoices.length > 0
  const searchViaNow =
    searchVia ??
    (searchChoices.some((c) => c.id === agentDefault.searchVia)
      ? agentDefault.searchVia
      : searchChoices[0]?.id ?? '')

  // --- the thread --------------------------------------------------------------

  // Open on the most recent conversation. Landing on a blank composer when
  // there is history behind it reads as having lost it.
  useEffect(() => {
    if (!threadId && threads.data?.length) setThreadId(threads.data[0].thread_id)
  }, [threads.data, threadId])

  const messages: AgentMessage[] = thread.data?.messages ?? []

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages.length, inFlight])

  // Once the thread has the question, the server's copy replaces ours.
  const showInFlight =
    inFlight !== null &&
    !messages.some((m) => m.role === 'user' && m.content === inFlight.question)

  /** The evidence panel follows the last answer unless something is pinned. */
  const evidence = useMemo(() => {
    const last = [...messages].reverse().find((m) => m.role === 'assistant')
    return last?.sources ?? []
  }, [messages])

  // Evidence is only worth a panel once there is some; when an answer brings
  // sources, the panel comes forward on its own.
  const evidenceCount = evidence.length
  useEffect(() => {
    if (evidenceCount > 0) dock.reveal('evidence')
  }, [evidenceCount, dock])

  // --- files ----------------------------------------------------------------------

  const addFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files)
      if (!list.length) return
      if (attachments.length + list.length > 8) {
        toast.error(new Error('Up to 8 files can be attached to one question.'))
        return
      }
      setReading((n) => n + list.length)
      for (const file of list) {
        try {
          const read = await readFile.mutateAsync(file)
          setAttachments((prev) => (prev.some((a) => a.name === read.name) ? prev : [...prev, read]))
        } catch (error) {
          toast.error(error)
        } finally {
          setReading((n) => n - 1)
        }
      }
      inputRef.current?.focus()
    },
    [attachments.length, readFile, toast],
  )

  // --- asking -----------------------------------------------------------------------

  async function send() {
    const question = draft.trim()
    if (!question || !agentId) return
    const sent = attachments
    setDraft('')
    setAttachments([])
    setInFlight({ question, files: sent.map((a) => a.name) })
    try {
      const answer = await ask.mutateAsync({
        question,
        thread_id: threadId,
        // Only what this turn chose for itself. Sending the agent's own model
        // back to it would be harmless today and a stale copy the day the
        // agent is reconfigured with the tab still open.
        ...(pick ? { model: pick.model, llm_connection_id: pick.source === 'service' ? null : pick.source } : {}),
        ...(modePick ? { mode: modePick } : {}),
        ...(searchVia ? { search_connection_id: searchVia === 'service' ? null : searchVia } : {}),
        ...(graphPick ? { graph_ids: graphPick } : {}),
        attachments: sent.map((a) => ({ name: a.name, text: a.text })),
      })
      setThreadId(answer.thread.thread_id)
    } catch (error) {
      toast.error(error)
      setDraft(question)
      setAttachments(sent)
    } finally {
      setInFlight(null)
    }
  }

  if (agent.isLoading) return <Loading label="Opening the agent…" />
  if (agent.error) {
    return (
      <div className="page">
        <ErrorNotice error={agent.error} onRetry={agent.refetch} />
        <button className="btn" onClick={() => navigate('/agents')}>
          Back to agents
        </button>
      </div>
    )
  }
  if (!agent.data || !agentId) return null

  const missing = agent.data.missing_graphs ?? []
  const graphNames = readGraphs.map((g) => g.name ?? 'a graph')
  const isOpen = graphIds.length === 0
  const empty = messages.length === 0 && !showInFlight

  // The + menu's own section: which graphs this conversation reads.
  const graphMenu = (
    <>
      <div className="ch-panelhead">
        Knowledge graphs
        {graphPick && <span className="ch-panelhead-sub">this conversation</span>}
      </div>
      {allGraphs.length === 0 && (
        <p className="ch-panelnote">
          No graphs yet — <Link to="/brain">create one</Link> and it appears here. Until then the
          agent answers openly.
        </p>
      )}
      {allGraphs.map((g) => {
        const on = graphIds.includes(g.graph_id)
        return (
          <button
            key={g.graph_id}
            type="button"
            className="ch-item ch-item-sm"
            role="menuitemcheckbox"
            aria-checked={on}
            onClick={() =>
              setGraphPick(on ? graphIds.filter((id) => id !== g.graph_id) : [...graphIds, g.graph_id])
            }
          >
            <span className="ch-itemtext">
              <span>{g.name}</span>
              {g.description && <span className="ch-itemsub">{g.description}</span>}
            </span>
            {on && (
              <span className="ch-check" aria-hidden="true">
                ✓
              </span>
            )}
          </button>
        )
      })}
      {allGraphs.length > 0 && (
        <p className="ch-panelnote">
          {isOpen
            ? 'None chosen: it answers from the model, the web and files, and says so.'
            : 'Grounded: every answer comes from these, cited, or is refused.'}
          {graphPick && (
            <>
              {' '}
              <button type="button" className="ch-link" onClick={() => setGraphPick(null)}>
                Back to the agent's own
              </button>
            </>
          )}
        </p>
      )}
    </>
  )

  const modelPicker = (
    <ModelPicker
      groups={groups}
      source={source}
      model={model}
      onPick={(key, id) => setPick({ source: key, model: id })}
      connectors={options.data?.llm_connectors ?? []}
      returnTo={location.pathname}
      drop="up"
    />
  )

  return (
    <div className="cp-screen ag-screen">
      <Dock
        className="ag-chat"
        left={dock.state.threads}
        right={dock.state.evidence}
        sizes={{ left: 240, right: 320 }}
      >
        {/* ------------------------------------------------ conversations */}
        <DockPanel
          side="left"
          title="Conversations"
          icon={<Icon.chat />}
          state={dock.state.threads}
          onState={(st) => dock.set('threads', st)}
          summary={threads.data?.length ? <span className="badge badge-neutral">{threads.data.length}</span> : undefined}
        >
        <div className="ag-threads">
          <button
            className="btn btn-primary btn-sm ag-newthread"
            onClick={() => {
              setThreadId(undefined)
              setDraft('')
              setFocused(null)
            }}
          >
            New conversation
          </button>

          {threads.isLoading && <Loading label="Loading…" />}
          {threads.data?.length === 0 && (
            <p className="small faint" style={{ padding: '8px 4px' }}>
              Nothing yet. Ask something below.
            </p>
          )}

          {threads.data?.map((row) => (
            <div className="ag-threadrow" key={row.thread_id} data-on={row.thread_id === threadId}>
              <button className="ag-threadbtn" onClick={() => setThreadId(row.thread_id)}>
                <span className="truncate">{row.title}</span>
                <span className="small faint">{relativeTime(row.updated_at)}</span>
              </button>
              <button
                className="ag-threaddel"
                aria-label={`Delete ${row.title}`}
                title="Delete this conversation"
                onClick={async () => {
                  try {
                    await removeThread.mutateAsync(row.thread_id)
                    if (row.thread_id === threadId) setThreadId(undefined)
                  } catch (error) {
                    toast.error(error)
                  }
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
        </DockPanel>

        {/* ------------------------------------------------------- thread */}
        <DockMain>
        <section className="ag-main">
          <header className="ag-head">
            <span className="ag-avatar" aria-hidden="true">
              {agent.data.avatar || '◈'}
            </span>
            <div className="ag-headname">
              <h2>{agent.data.name}</h2>
              <span className="small faint mono" title={pick ? 'Chosen for this conversation' : 'As configured'}>
                {chosen?.label ?? model}
                {pick && ' · this conversation'}
              </span>
            </div>
            <span className="cp-spacer" />
            {isOpen ? (
              <Chip tone="warn">open · no graph</Chip>
            ) : (
              readGraphs.map((graph) => (
                <Chip key={graph.graph_id} tone={graph.name ? 'plain' : 'err'}>
                  {graph.name ?? 'missing graph'}
                  {graphPick ? ' ·' : ''}
                </Chip>
              ))
            )}
            <button className="btn btn-sm" onClick={() => setConfiguring(true)}>
              Configure
            </button>
            <Link className="btn btn-ghost btn-sm" to="/agents">
              All agents
            </Link>
          </header>

          {missing.length > 0 && (
            <div className="callout callout-warn" style={{ margin: '0 14px' }}>
              <p className="small" style={{ margin: 0 }}>
                {missing.length} of this agent's knowledge graphs no longer exists, so it can
                see less than it was configured to. Open Configure and point it somewhere
                that is still there.
              </p>
            </div>
          )}

          <div className="ag-thread" data-empty={empty || undefined}>
            {empty && (
              <div className="ag-empty">
                <span className="ag-avatar ag-avatar-lg" aria-hidden="true">
                  {agent.data.avatar || '◈'}
                </span>
                <p className="ag-emptytitle">Ask {agent.data.name} something</p>
                <p className="muted small">
                  {agent.data.description ||
                    (isOpen
                      ? 'No graph is connected, so it answers openly — add one from the + menu to ground it.'
                      : `It answers from ${graphNames.join(' and ')} and nothing else.`)}
                </p>
                <div className="ch-hints">
                  {MODES.map((m) => (
                    <button
                      key={m.id}
                      className="ch-hint"
                      data-on={mode === m.id || undefined}
                      disabled={!canSearch}
                      title={canSearch ? `${m.hint} — alongside ${graphNames.join(' and ')}` : 'Connect Tavily to search the web'}
                      onClick={() => {
                        setModePick(mode === m.id ? 'chat' : m.id)
                        inputRef.current?.focus()
                      }}
                    >
                      <span aria-hidden="true">{m.icon}</span> {m.label}
                    </button>
                  ))}
                  <button className="ch-hint" onClick={() => inputRef.current?.focus()} title="Or drop files onto the box">
                    <span aria-hidden="true">＋</span> Add files
                  </button>
                  {isOpen && allGraphs.length > 0 && (
                    <button
                      className="ch-hint"
                      onClick={() => setGraphPick([allGraphs[0].graph_id])}
                      title="Ground this conversation in a knowledge graph"
                    >
                      <span aria-hidden="true">◇</span> Read {allGraphs[0].name}
                    </button>
                  )}
                </div>
              </div>
            )}

            {messages.map((message) => (
              <Turn
                key={message.message_id}
                message={message}
                onCite={(source) => setFocused(source)}
              />
            ))}

            {showInFlight && (
              <div className="ag-turn" data-role="user">
                <div className="ag-bubble">{inFlight.question}</div>
                {inFlight.files.length > 0 && (
                  <div className="ag-meta small faint">
                    {inFlight.files.map((name) => (
                      <span key={name} className="ag-file">
                        <FileIcon /> {name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            {showInFlight && (
              <div className="ag-turn" data-role="assistant">
                <div className="ag-bubble ag-pending">
                  <Dots />{' '}
                  {isOpen
                    ? mode !== 'chat' && canSearch
                      ? 'Searching the web…'
                      : 'Thinking…'
                    : mode !== 'chat' && canSearch
                      ? `Reading ${graphNames.join(' and ')} and searching the web…`
                      : `Reading ${graphNames.join(' and ')}…`}
                </div>
              </div>
            )}

            <div ref={endRef} />
          </div>

          <div className="ag-dock">
            <Composer
              drop="up"
              draft={draft}
              onDraft={setDraft}
              onSend={send}
              disabled={!model}
              pending={ask.isPending}
              placeholder={
                MODES.find((m) => m.id === mode)
                  ? `${MODES.find((m) => m.id === mode)?.label}: ask ${agent.data.name}…`
                  : `Ask ${agent.data.name}…`
              }
              inputRef={inputRef}
              attachments={attachments}
              reading={reading}
              onRemove={(name) => setAttachments((prev) => prev.filter((a) => a.name !== name))}
              onFiles={addFiles}
              mode={mode}
              onMode={(m) => setModePick(m)}
              canSearch={canSearch}
              searchChoices={searchChoices}
              searchVia={searchViaNow}
              onSearchVia={setSearchVia}
              searchConnectorId={options.data?.search_connector_id ?? 'tavily'}
              modelPicker={modelPicker}
              menuExtra={graphMenu}
              menuTitle="Files, knowledge graphs, web search"
            />
          </div>
        </section>
        </DockMain>

        {/* ----------------------------------------------------- evidence */}
        <DockPanel
          side="right"
          title="Evidence"
          icon={<Icon.layers />}
          state={dock.state.evidence}
          onState={(st) => dock.set('evidence', st)}
          summary={evidence.length ? <span className="badge badge-neutral">{evidence.length}</span> : undefined}
        >
        <div className="ag-evidence">
          {evidence.length === 0 ? (
            <p className="small faint">
              {isOpen
                ? 'No graph is being read. Web pages and files the turn adds appear here, cited; choose a graph from the + menu to ground the answers.'
                : `What the last answer was shown appears here — the records and documents from ${graphNames.join(
                    ' and ',
                  )}, plus any web pages or files this turn added — with the ones it cited marked.`}
            </p>
          ) : (
            evidence.map((source) => (
              <SourceCard
                key={`${source.index}-${source.ref_id}`}
                source={source}
                focused={focused?.index === source.index}
              />
            ))
          )}
        </div>
        </DockPanel>
      </Dock>

      {configuring && (
        <AgentDialog
          agent={agent.data}
          onClose={() => setConfiguring(false)}
          onSaved={() => setConfiguring(false)}
        />
      )}
    </div>
  )
}

/**
 * The site a web source came from.
 *
 * A bare host is what tells you whether to believe it; the full URL is long
 * enough to push the excerpt off the card and says less.
 */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

/** One turn: the person's question, or the agent's answer with live citations. */
function Turn({
  message,
  onCite,
}: {
  message: AgentMessage
  onCite: (source: AgentSource) => void
}) {
  const [showThinking, setShowThinking] = useState(false)
  const byIndex = useMemo(
    () => new Map(message.sources.map((s) => [s.index, s])),
    [message.sources],
  )
  // Only numbers the answer was actually given become chips. A `[7]` when the
  // agent was shown five sources is a model writing a number, not a citation.
  const cited = useMemo(() => new Set(byIndex.keys()), [byIndex])
  const thinking = message.meta?.thinking

  if (message.role === 'user') {
    return (
      <div className="ag-turn" data-role="user">
        <div className="ag-bubble">{message.content}</div>
        {message.meta?.attachments && message.meta.attachments.length > 0 && (
          <div className="ag-meta small faint">
            {message.meta.attachments.map((name) => (
              <span key={name} className="ag-file">
                <FileIcon /> {name}
              </span>
            ))}
          </div>
        )}
      </div>
    )
  }

  const activeMode = MODES.find((m) => m.id === message.meta?.mode)

  return (
    <div className="ag-turn" data-role="assistant" data-refused={message.refused || undefined}>
      {thinking && (
        <button type="button" className="ag-thinktoggle small faint" onClick={() => setShowThinking((v) => !v)}>
          {showThinking ? 'Hide reasoning' : 'Show reasoning'}
        </button>
      )}
      {thinking && showThinking && <div className="ag-thinking small muted">{thinking}</div>}

      <div className="ag-bubble ag-bubble-md">
        <Markdown
          text={message.content}
          cited={cited}
          onCite={(n) => {
            const source = byIndex.get(n)
            if (source) onCite(source)
          }}
        />
      </div>

      <div className="ag-meta small faint">
        {message.refused ? (
          <span title="The graph held no evidence, so nothing was invented.">
            no evidence in the graph
          </span>
        ) : message.meta?.open ? (
          <span title="No knowledge graph was read for this answer.">
            open answer{message.citations.length ? ` · ${message.citations.length} citation${message.citations.length === 1 ? '' : 's'}` : ''}
          </span>
        ) : (
          <span>
            {message.citations.length} citation
            {message.citations.length === 1 ? '' : 's'}
          </span>
        )}
        {message.model && <span className="mono"> · {message.model}</span>}
        {message.meta?.duration_ms != null && <span> · {message.meta.duration_ms}ms</span>}
        {/* Only when it actually searched. "searched the web: no" under every
            answer from every agent is noise; a search that was asked for and
            did not happen is the one case worth saying out loud. */}
        {message.meta?.web_searched && (
          <span title={message.meta.web_error ?? 'Pages found on the web'}>
            {' · '}
            {activeMode && <span aria-hidden="true">{activeMode.icon} </span>}
            {message.meta.web_error
              ? 'web search failed'
              : `${message.meta.web_results ?? 0} web result${
                  message.meta.web_results === 1 ? '' : 's'
                }`}
          </span>
        )}
        {message.meta?.attachments && message.meta.attachments.length > 0 && (
          <span>
            {' · '}
            {message.meta.attachments.length} file{message.meta.attachments.length === 1 ? '' : 's'} read
          </span>
        )}
      </div>
    </div>
  )
}

function SourceCard({ source, focused }: { source: AgentSource; focused: boolean }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [focused])

  const body = (
    <>
      <div className="ag-srchead">
        <span className="ag-srcn">{source.index}</span>
        <span className="truncate">{source.title}</span>
        {/* Whose fact this is. A page off the open web, or a file handed over
            for this question, sitting unlabelled beside a record from the
            user's own systems is the one confusion this feature must not
            create. */}
        {source.kind === 'web' && <span className="ag-srctag">web</span>}
        {source.kind === 'file' && <span className="ag-srctag">file</span>}
      </div>
      {source.kind === 'web' && source.url && (
        <p className="small faint truncate mono">{hostOf(source.url)}</p>
      )}
      <p className="small muted">{source.excerpt}</p>
    </>
  )

  return (
    <div className="ag-source" data-kind={source.kind} data-focused={focused || undefined} ref={ref}>
      {/* A record is a place you can go, and so is a web page — a chunk of a
          document or an attached file is not, so it stays plain. */}
      {source.kind === 'record' && source.graph_id ? (
        <Link to={`/brain/${source.graph_id}/records/${source.ref_id}`}>{body}</Link>
      ) : source.kind === 'web' && source.url ? (
        <a href={source.url} target="_blank" rel="noreferrer noopener">
          {body}
        </a>
      ) : (
        body
      )}
    </div>
  )
}
